import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Input,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Link, createFileRoute, redirect } from "@tanstack/react-router"
import { useDeferredValue, useMemo, useState } from "react"

import PlatformShell from "../../components/PlatformAdmin/PlatformShell"
import { listPlatformTenants } from "../../client/platform"
import { isPlatformLoggedIn, isTenantSubdomainHost } from "../../utils/platformAuth"

export const Route = createFileRoute("/platform/usage")({
  component: PlatformUsagePage,
  beforeLoad: async () => {
    if (isTenantSubdomainHost()) {
      throw redirect({ to: "/login" })
    }
    if (!isPlatformLoggedIn()) {
      throw redirect({ to: "/platform/login" })
    }
  },
})

function PlatformUsagePage() {
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("active")
  const deferredSearch = useDeferredValue(search)

  const usageQuery = useQuery({
    queryKey: ["platformAdmin", "usage", deferredSearch, status],
    queryFn: () => listPlatformTenants({ search: deferredSearch || undefined, status: status || undefined }),
  })

  const totals = useMemo(() => {
    const rows = usageQuery.data?.data ?? []
    return rows.reduce(
      (acc, tenant) => {
        acc.tenants += 1
        acc.activeUsers += tenant.usage_snapshot?.active_users_count ?? tenant.active_users_count ?? 0
        acc.totalUsers += tenant.usage_snapshot?.total_users_count ?? 0
        acc.documents += tenant.usage_snapshot?.documents_count ?? 0
        acc.storageBytes += tenant.usage_snapshot?.storage_used_bytes ?? 0
        return acc
      },
      {
        tenants: 0,
        activeUsers: 0,
        totalUsers: 0,
        documents: 0,
        storageBytes: 0,
      },
    )
  }, [usageQuery.data])

  return (
    <PlatformShell
      title="Usage"
      description="Zbiorczy widok aktywności, użytkowników i storage dla tenantów SaaS."
    >
      <Stack spacing={5}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Input
            placeholder="Search by company, subdomain or admin email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="active">Active only</option>
            <option value="">All statuses</option>
            <option value="suspended">Suspended</option>
            <option value="archived">Archived</option>
          </Select>
        </SimpleGrid>

        {usageQuery.isLoading ? (
          <Stack spacing={3}>
            <Skeleton h="48px" />
            <Skeleton h="48px" />
            <Skeleton h="48px" />
          </Stack>
        ) : null}

        {usageQuery.isError ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <AlertDescription>Failed to load usage data.</AlertDescription>
          </Alert>
        ) : null}

        {usageQuery.data ? (
          <>
            <SimpleGrid columns={{ base: 2, xl: 5 }} spacing={4}>
              <UsageStat label="Tenants" value={String(totals.tenants)} />
              <UsageStat label="Active users" value={String(totals.activeUsers)} />
              <UsageStat label="Total users" value={String(totals.totalUsers)} />
              <UsageStat label="Documents" value={String(totals.documents)} />
              <UsageStat label="Storage" value={formatBytes(totals.storageBytes)} />
            </SimpleGrid>

            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Tenant</Th>
                    <Th>Status</Th>
                    <Th isNumeric>Active users</Th>
                    <Th isNumeric>Total users</Th>
                    <Th isNumeric>Documents</Th>
                    <Th isNumeric>Storage</Th>
                    <Th isNumeric>API month</Th>
                    <Th>Last activity</Th>
                    <Th>Snapshot</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {usageQuery.data.data.map((tenant) => (
                    <Tr key={tenant.id}>
                      <Td>
                        <Link
                          to="/platform/spaces/$tenantId"
                          params={{ tenantId: tenant.id }}
                          style={{ fontWeight: 600, color: "inherit" }}
                        >
                          {tenant.name || tenant.subdomain}
                        </Link>
                        <Text fontSize="xs" color="gray.500">{tenant.subdomain}</Text>
                      </Td>
                      <Td>
                        <Badge colorScheme={tenant.lifecycle_status === "active" ? "green" : tenant.lifecycle_status === "suspended" ? "red" : "gray"}>
                          {tenant.lifecycle_status}
                        </Badge>
                      </Td>
                      <Td isNumeric>{tenant.usage_snapshot?.active_users_count ?? tenant.active_users_count ?? 0}</Td>
                      <Td isNumeric>{tenant.usage_snapshot?.total_users_count ?? 0}</Td>
                      <Td isNumeric>{tenant.usage_snapshot?.documents_count ?? 0}</Td>
                      <Td isNumeric>{formatBytes(tenant.usage_snapshot?.storage_used_bytes ?? 0)}</Td>
                      <Td isNumeric>{tenant.usage_snapshot?.api_calls_month ?? 0}</Td>
                      <Td>{formatMaybeDate(tenant.usage_snapshot?.last_user_seen_at)}</Td>
                      <Td>{formatMaybeDate(tenant.usage_snapshot?.snapshot_taken_at)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </>
        ) : null}
      </Stack>
    </PlatformShell>
  )
}

function UsageStat({ label, value }: { label: string; value: string }) {
  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
      <Stat>
        <StatLabel>{label}</StatLabel>
        <StatNumber fontSize="2xl">{value}</StatNumber>
      </Stat>
    </Box>
  )
}

function formatMaybeDate(value?: string | null) {
  if (!value) return "n/a"
  return new Date(value).toLocaleString()
}

function formatBytes(value: number) {
  if (!value) return "0 B"
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
